/* eslint-disable @typescript-eslint/no-empty-function */
import XTable from '../components/x-table/XTable.vue'
import { Column, TableInstance } from 'element-plus'
import { BaseController } from './BaseController'
import { SortableEvent } from "sortablejs";

const enum EVENT_NAME {
  'mounte' = 'mounted',
  'select' = 'select',
  'select-all' = 'select-all',
  'selection-change' = 'selection-change',
  'cell-mouse-enter' = 'cell-mouse',
  'cell-mouse-leave' = 'cell-mouse',
  'cell-contextmenu' = 'cell-contextmenu',
  'cell-click' = 'cell-click',
  'cell-dblclick' = 'cell-dblclick',
  'row-click' = 'row-click',
  'row-contextmenu' = 'row-contextmenu',
  'row-dblclick' = 'row-dblclick',
  'header-click' = 'header-click',
  'header-contextmenu' = 'header-contextmenu',
  'sort-change' = 'sort-change',
  'filter-change' = 'filter-change',
  'current-change' = 'current-change',
  'header-dragend' = 'header-dragend',
  'expand-change' = 'expand-change',
  'size-change' = 'size-change',
  'page-change' = 'page-change'
}

/**
 *表格实例
 * @export
 * @class Table
 */
export class Table extends BaseController {
  /**
   * 构造表格
   * @param {Element} el 挂载节点
   * @param {TableColumnProp[]} column 表格行
   * @param {TableHeaderButton[]} headerButton 表格头部按钮
   * @param {TableProp} [elTableProps] el-table属性
   * @param {string} [url] 请求地址
   * @param {object} [requestData] 请求时的额外参数
   * @param {boolean} [full] 是否占满内容区
   * @param {boolean} [showPagination] 是否开启分页
   * @param {{ data: string; total: string }} [responseConfig] 响应数据配置
   * @memberof Table
   */
  constructor(
    el: Element,
    column: TableColumnProp[],
    headerButton?: TableHeaderButton[] | ((tableData: Ref<any[]>) => VNode),
    elTableProps?: TableProp,
    url?: string,
    otherProps?: { 
      requestData?: object, 
      full?: boolean, 
      showPagination?: boolean, 
      responseConfig?: { 
        data: string | ((data: any) => any[]); 
        total: string | ((data: any) => number)
      },
      autoLoad?: boolean
      selectable?: boolean
      showIndex?: boolean
      dragable?: boolean
    },
  ) {
    super(el, XTable, {
      column,
      headerButtons: headerButton || [],
      elTableProps: elTableProps || {},
      url: url || '',
      ...otherProps
    })
  }

  /**
   * 加载表格数据
   * @param param 请求参数 
   * @param reset 是否重置页码 
   */
  load(param?: any, reset = true) {
    if(this.controller) {
      this.controller.load(param, reset)
    }
  }

  /**
   * 设置表格数据
   * @param data 数据
   */
  setTableData(data: any[]) {
    if(this.controller) {
      this.controller.setTableData(data)
    }
  }

  /**
   * 设置分页
   * @param pagination 分页数据
   */
  setPagination(pagination: { pageNumber?: number, pageSize?: number, total?: number }) {
    if(this.controller) {
      this.controller.setPagination(pagination)
    }
  }

  /**
   * 设置请求参数
   * @param data 请求参数
   */
  setRequestData(data: any) {
    if(this.controller) {
      this.controller.setRequestData(data)
    }
  }

  /**
   * 设置表格选中行key
   */
  setSelectRowKeys(keys: (number | string)[],tabelKey = 'id') {
    if(this.controller) {
      this.controller.setSelectRowKeys(keys,tabelKey)
    }
  }

  /**
   * 获取选中项
   * @returns 
   */
  getSelectRows(): any[] {
    if(this.controller) {
      return this.controller.getSelectRows()
    }
    return []
  }

  /**
   * 获取el-table表格示例
   * @returns 
   */
  getElTableInstance(): TableInstance | null {
    if(this.controller) {
      return this.controller.getElTableInstance()
    }
    return null
  }

  /**
   * 获取表格数据
   */
  getTableData(): any[] {
    if(this.controller) {
      return this.controller.getTableData() || []
    }
    return []
  }

  /**
   * 拖拽事件
   * @memberof Table
   */
  get onDragEnd(): (evt: SortableEvent) => void {
    return this.handler['dragEnd']!
  }

  set onDragEnd(fn) {
    this.handler['dragEnd'] = fn
  }

  /**
   * 加载成功后的事件
   * @memberof Table
   */
  get onLoaded(): (tableData: any[]) => void {
    return this.handler['loaded']!
  }
  
  set onLoaded(fn) {
    this.handler['loaded'] = fn
  }

  /**
   * 当用户手动勾选数据行的 Checkbox 时触发的事件
   * @memberof Table
   */
  get onSelect(): (selection: any[], row: any) => void {
    return this.handler[EVENT_NAME.select]!
  }

  set onSelect(fn) {
    this.handler[EVENT_NAME.select] = fn
  }

  /**
   * 当用户手动勾选全选 Checkbox 时触发的事件
   * @memberof Table
   */
  get onSelectAll(): (selection: any[]) => void {
    return this.handler[EVENT_NAME['select-all']]!
  }

  set onSelectAll(fn) {
    this.handler[EVENT_NAME['select-all']] = fn
  }

  /**
   * 当选择项发生变化时会触发该事件
   * @memberof Table
   */
  get onSelectionChange(): (selection: any[]) => void {
    return this.handler[EVENT_NAME['selection-change']]!
  }

  set onSelectionChange(fn) {
    this.handler[EVENT_NAME['selection-change']] = fn
  }
  
  /**
   * 当单元格 hover 进入时会触发该事件
   * @memberof Table
   */
  get onCellMouseEnter(): (row: any, column: Column, cell: Element, event: MouseEvent) => void {
    return this.handler[EVENT_NAME['cell-mouse-enter']]!
  }

  set onCellMouseEnter(fn) {
    this.handler[EVENT_NAME['cell-mouse-enter']] = fn
  }

  /**
   * 当单元格 hover 退出时会触发该事件
   * @memberof Table
   */
  get onCellMouseLeave(): (row: any, column: Column, cell: Element, event: MouseEvent) => void {
    return this.handler[EVENT_NAME['cell-mouse-leave']]!
  }

  set onCellMouseLeave(fn) {
    this.handler[EVENT_NAME['cell-mouse-leave']] = fn
  }

  /**
   * 当某个单元格被点击时会触发该事件
   * @memberof Table
   */
  get onCellClick(): (row: any, column: Column, cell: Element, event: Event) => void {
    return this.handler[EVENT_NAME['cell-click']]!
  }

  set onCellClick(fn) {
    this.handler[EVENT_NAME['cell-click']] = fn
  }

  /**
   * 当某个单元格被双击击时会触发该事件
   * @memberof Table
   */
  get onCellDblclick(): (row: any, column: Column, cell: Element, event: Event) => void {
    return this.handler[EVENT_NAME['cell-dblclick']]!
  }

  set onCellDblclick(fn) {
    this.handler[EVENT_NAME['cell-dblclick']] = fn
  }
  
  /**
   * 当某个单元格被鼠标右键点击时会触发该事件
   * @memberof Table
   */
  get onCellContextmenu(): (row: any, column: Column, cell: Element, event: Event) => void {
    return this.handler[EVENT_NAME['cell-contextmenu']]!
  }

  set onCellContextmenu(fn) {
    this.handler[EVENT_NAME['cell-contextmenu']] = fn
  }

  /**
   * 当某一行被点击时会触发该事件
   * @memberof Table
   */
  get onRowClick(): (row: any, column: Column, event: Event) => void {
    return this.handler[EVENT_NAME['row-click']]!
  }

  set onRowClick(fn) {
    this.handler[EVENT_NAME['row-click']] = fn
  }
  
  /**
   * 当某一行被鼠标右键点击时会触发该事件
   * @memberof Table
   */
  get onRowContextmenu(): (row: any, column: Column, event: Event) => void {
    return this.handler[EVENT_NAME['row-contextmenu']]!
  }

  set onRowContextmenu(fn) {
    this.handler[EVENT_NAME['row-contextmenu']] = fn
  }

  /**
   * 当某一行被双击时会触发该事件
   * @memberof Table
   */
  get onRowDblclick(): (row: any, column: Column, event: Event) => void {
    return this.handler[EVENT_NAME['row-click']]!
  }

  set onRowDblclick(fn) {
    this.handler[EVENT_NAME['row-click']] = fn
  }

  /**
   * 当某一列的表头被点击时会触发该事件
   * @memberof Table
   */
  get onHeaderClick(): (column: Column, event: Event) => void {
    return this.handler[EVENT_NAME['header-click']]!
  }

  set onHeaderClick(fn) {
    this.handler[EVENT_NAME['header-click']] = fn
  }

  /**
   * 当某一列的表头被鼠标右键点击时触发该事件
   * @memberof Table
   */
  get onHeaderContextmenu(): (column: Column, event: Event) => void {
    return this.handler[EVENT_NAME['header-contextmenu']]!
  }

  set onHeaderContextmenu(fn) {
    this.handler[EVENT_NAME['header-contextmenu']] = fn
  }
  
  /**
   * 当表格的排序条件发生变化的时候会触发该事件
   * @memberof Table
   */
  get onSortChange(): (value: { column: Column, prop: any, order: number }) => void {
    return this.handler[EVENT_NAME['sort-change']]!
  }

  set onSortChange(fn) {
    this.handler[EVENT_NAME['sort-change']] = fn
  }

  /**
   * column 的 key， 如果需要使用 filter-change 事件，则需要此属性标识是哪个 column 的筛选条件
   * @memberof Table
   */
  get onFilterChange(): (filters: any[]) => void {
    return this.handler[EVENT_NAME['filter-change']]!
  }

  set onFilterChange(fn) {
    this.handler[EVENT_NAME['filter-change']] = fn
  }
  
  /**
   * 当表格的当前行发生变化的时候会触发该事件，如果要高亮当前行，请打开表格的 highlight-current-row 属性
   * @memberof Table
   */
  get onCurrentChange(): (currentRow: any, oldCurrentRow: any) => void {
    return this.handler[EVENT_NAME['current-change']]!
  }

  set onCurrentChange(fn) {
    this.handler[EVENT_NAME['current-change']] = fn
  }
  
  /**
   * 当拖动表头改变了列的宽度的时候会触发该事件
   * @memberof Table
   */
  get onHeaderDragend(): (newWidth: number, oldWidth: number, column: Column, event: DragEvent) => void {
    return this.handler[EVENT_NAME['header-dragend']]!
  }

  set onHeaderDragend(fn) {
    this.handler[EVENT_NAME['header-dragend']] = fn
  }

  /**
   *当用户对某一行展开或者关闭的时候会触发该事件（展开行时，回调的第二个参数为 expandedRows；树形表格时第二参数为 expanded）
   * @memberof Table
   */
  get onExpandChange(): (row: any, expanded: any) => void {
    return this.handler[EVENT_NAME['expand-change']]!
  }

  set onExpandChange(fn) {
    this.handler[EVENT_NAME['expand-change']] = fn
  }

  /**
   * 分页改变
   * @memberof Table
   */
  get onSizeChange(): (row: any, expanded: any) => void {
    return this.handler[EVENT_NAME['size-change']]!
  }

  set onSizeChange(fn) {
    this.handler[EVENT_NAME['size-change']] = fn
  }

  /**
   * 页码改变
   * @memberof Table
   */
   get onPageChange(): (row: any, expanded: any) => void {
    return this.handler[EVENT_NAME['page-change']]!
  }

  set onPageChange(fn) {
    this.handler[EVENT_NAME['page-change']] = fn
  }
}

